import React from 'react';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  Activity,
  Eye,
  MessageSquare,
  Heart,
  Settings
} from 'lucide-react';

const AdminSidebarTest = () => {
  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Admin Dashboard with Sidebar
          </h1>
          <p className="text-gray-600">
            Testing the new admin sidebar navigation based on your design
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Members</p>
                <p className="text-2xl font-bold text-gray-900">1,234</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Events This Month</p>
                <p className="text-2xl font-bold text-gray-900">24</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Growth Rate</p>
                <p className="text-2xl font-bold text-gray-900">+12%</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Activity className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Today</p>
                <p className="text-2xl font-bold text-gray-900">89</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <Card className="lg:col-span-2 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              <Button variant="outline" size="sm">
                View All
              </Button>
            </div>
            <div className="space-y-4">
              {[
                { type: 'event', title: 'Shabbat Dinner', time: '2 hours ago', icon: Calendar },
                { type: 'member', title: 'New member joined', time: '4 hours ago', icon: Users },
                { type: 'prayer', title: 'Prayer request added', time: '6 hours ago', icon: Heart },
                { type: 'class', title: 'Torah Study updated', time: '1 day ago', icon: MessageSquare }
              ].map((activity, index) => {
                const Icon = activity.icon;
                return (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="p-2 bg-gray-100 rounded-full">
                      <Icon className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                    <Badge variant="secondary">{activity.type}</Badge>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Button className="w-full justify-start" variant="outline">
                <Users className="w-4 h-4 mr-2" />
                Manage Members
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <Calendar className="w-4 h-4 mr-2" />
                Create Event
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <MessageSquare className="w-4 h-4 mr-2" />
                Add Class
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <Heart className="w-4 h-4 mr-2" />
                Prayer Requests
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
          </Card>
        </div>

        {/* Navigation Test Section */}
        <Card className="mt-8 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sidebar Navigation Test</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-4">
              The sidebar on the left includes:
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• <strong>Dashboard</strong> - Main admin overview (this page)</li>
              <li>• <strong>Communities</strong> - Group management</li>
              <li>• <strong>Members</strong> - Member administration</li>
              <li>• <strong>Analytics</strong> - Performance metrics (coming soon)</li>
              <li>• <strong>Growth</strong> - Growth tracking (coming soon)</li>
              <li>• <strong>Account</strong> - Admin profile settings</li>
              <li>• <strong>Help</strong> - Support and documentation (coming soon)</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              <strong>Group Content Section:</strong> When you have an active community, you'll see additional navigation for Upcoming, Events, Classes, Prayer, and CharityBox.
            </p>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSidebarTest;
